import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Box, Hash, Clock, ArrowRightLeft } from 'lucide-react';

import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'

import {
	Pagination,
	PaginationContent,
	PaginationItem,
	PaginationNext,
	PaginationPrevious,
} from '@/components/ui/pagination'

import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	CardDescription,
} from '@/components/ui/card'

import { useTransactionBlockRecents } from "@/hooks/useApi";
import { formatUnixTimestamp } from '@/utils/format';

const PAGE_SIZE = 20;
const MAX_ENTRIES = 10_000;

const TransactionBlocks = () => {
	const pageLimit = MAX_ENTRIES / PAGE_SIZE;
	const initialLoadCompleted = useRef<boolean>(false);

	const startingCursor = useRef<any>(null);
	const startingPageData = useRef<any>(null);

	const [cursor, setCursor] = useState<any>(null);
	const [currentPage, setCurrentPage] = useState<number>(1);

	const [pageToRender, setPageToRender] = useState<any>({});
	const [prevCursorHistory, setPrevCursorHistory] = useState<Array<string>>([]);
	const nextCursor = useRef<any>(null);

	const { data: transactionBlocksRecent } = useTransactionBlockRecents(20, cursor?.sequenceNumber ?? null, cursor?.txOffset ?? null);

	useEffect(() => {
		if (!initialLoadCompleted.current && transactionBlocksRecent?.cursor) {
			startingCursor.current = transactionBlocksRecent.cursor;
			startingPageData.current = transactionBlocksRecent;
			initialLoadCompleted.current = true;
		}

		if (transactionBlocksRecent?.cursor) {
			nextCursor.current = transactionBlocksRecent.cursor;
		}

		if (startingCursor.current === nextCursor.current) {
			setPageToRender(startingPageData.current);
		} else {
			setPageToRender(transactionBlocksRecent);
		}
	}, [transactionBlocksRecent])

	if (!transactionBlocksRecent) {
		return <>Loading...</>
	}

	return (
		<>
			<Pagination className='mb-2'>
				<PaginationContent>
					{/* Previous Button */}
					<PaginationItem>
						<PaginationPrevious 
							className='bg-[#C0E6FF]/15 backdrop-blur-md border hover:bg-[#C0E6FF]/10 hover:backdrop-blur-m'
							content={<span className='text-xs'>Prev</span> as any}
							onClick={() => {
								if (currentPage === 1) return;
								if (prevCursorHistory.length === 1) {
									setCursor(null);
								}

								setPrevCursorHistory((prev: any) => {
									const temp = [...prev];
									temp.pop();
									if (temp.length) setCursor(temp[temp.length - 1]);
									return temp;
								})
								setCurrentPage(prev => prev-1);
							}}
						/>
					</PaginationItem>

					{/* Next Button */}
					<PaginationItem>
						<PaginationNext 
							className='bg-[#C0E6FF]/15 backdrop-blur-md border hover:bg-[#C0E6FF]/10 hover:backdrop-blur-md'
							content={<span className='text-xs'>Next</span> as any}
							onClick={() => {
								if (currentPage === pageLimit) return;
								if (!nextCursor.current) return;
								setCursor(nextCursor.current);
								setPrevCursorHistory((prev: any) => [...prev, nextCursor.current]);
								setCurrentPage(prev => prev+1);
							}}
						/>
					</PaginationItem>
				</PaginationContent>
			</Pagination>
			<div className="rounded-lg border">
				<Table>
					<TableHeader className='bg-[#C0E6FF]/10 shadow-lg'>
						<TableRow className='pointer-events-none'>
							<TableHead className='text-white'>
								<div className='flex flex-row items-center'>
									<div className='flex-1 text-xs font-medium'>Checkpoint</div>
								</div>
							</TableHead>
							<TableHead className='text-white'>
								<div className='flex flex-row items-center'>
									<div className='flex-1 text-xs font-medium'>Digest</div>
								</div>
							</TableHead>
							<TableHead className='text-white'>
								<div className='flex flex-row items-center'>
									<div className='flex-1 text-xs font-medium'>Timestamp</div>
								</div>
							</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{pageToRender?.data?.map((checkpoint: any) => (
							<TableRow key={checkpoint.digest} className='hover:bg-[#C0E6FF]/10 hover:backdrop-blur-md'>
								<TableCell className='text-xs'>{checkpoint.checkpoint}</TableCell>
								<TableCell className='text-xs'>
									<Link to={`/transaction-blocks/${checkpoint.digest}`} className="flex items-center space-x-2 text-[#4DA2FF] hover:text-[#C0E6FF]">
										<span className='hidden md:block'>{`${checkpoint.digest.slice(0, 6)}...${checkpoint.digest.slice(-6)}`}</span>
										<span className='md:hidden'>{`${checkpoint.digest.slice(0, 6)}...`}</span>
									</Link>
								</TableCell>
								<TableCell className='text-xs'>{formatUnixTimestamp(Number(checkpoint.timestampMS))}</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>
		</>
	)

}

export const TransactionBlocksView = () => {
	return (
		<>
			<div className='flex-1 overflow-y-auto overflow-x-hidden flex max-w-full 
  			[&::-webkit-scrollbar-track]:bg-gray-100
  			[&::-webkit-scrollbar-thumb]:bg-gray-300
  			dark:[&::-webkit-scrollbar-track]:bg-neutral-700
  			dark:[&::-webkit-scrollbar-thumb]:bg-neutral-500'
			>
				<div className='flex flex-col gap-6 w-full h-full'>
					<div className="flex flex-col justify-center w-full gap-6 px-2 lg:px-4 py-5">
					<Card className='bg-transparent'>
						<CardHeader className="flex flex-col justify-start space-y-0 p-0 min-w-60">
							<div className='flex flex-row items-center px-2 pt-2'>
								<div className="rounded-full px-2 transition-colors">
									<ArrowRightLeft className='h-5 w-5' />
								</div>
								<div className='flex-1'>
									<CardTitle className="text-2xl font-medium">Transaction Blocks</CardTitle>
								</div>
							</div>
							<div className='px-4 pb-2'>
								<CardDescription className='text-md'>
									{`Showing most recent ${MAX_ENTRIES.toLocaleString()}`}
								</CardDescription>
							</div>
						</CardHeader>
						<CardContent className='px-2'>
							<TransactionBlocks />
						</CardContent>
					</Card>
					</div>
				</div>
			</div>
		</>
	)
}